/**
* ドライバーに紐づく予約・配車データ管理
*/

import axios from '@/plugins/axios';
import moment from 'moment';

// レースコンディション防止用フェッチID
let _reservationsFetchId = 0;
let _dispatchesFetchId = 0;
// 多重呼び出し防止フラグ
let _isFetching = false;

/** scheduleItems の計算ロジック（state + rootState から items 配列を生成） */
function buildScheduleItems(state, rootState) {
  const userState = rootState.user;
  const items = [];
  const toDateTimeStr = (date) => moment(date).format('YYYY-MM-DD HH:mm:ss');

  // 予約（通常 + デマンド）
  state.reservations.forEach((rsv) => {
    if (rsv.foreign_type === 31) { // デマンド
      if (!rsv.demandCustomers?.length) return;
      const first = rsv.demandCustomers[0];
      const start = rsv.demandCustomers
        .map((dc) => dc.p_datetime)
        .reduce((min, curr) => (curr < min ? curr : min), first.p_datetime);
      const end = rsv.demandCustomers
        .map((dc) => dc.d_datetime)
        .reduce((max, curr) => (curr > max ? curr : max), first.p_datetime);
      const startStr = toDateTimeStr(start);
      const endStr = toDateTimeStr(end);
      const duration = Math.ceil(
        (new Date(endStr).getTime() - new Date(startStr).getTime()) / (1000 * 60),
      );
      items.push({
        start: startStr,
        duration,
        type: 'reservation',
        id: rsv.id,
        raw: rsv,
        code: rsv.code,
      });
    } else {
      const start = toDateTimeStr(rsv.datetime || rsv.created_at);
      const duration = rsv.e_time / 60 || 30;
      items.push({
        start,
        duration,
        type: 'reservation',
        id: rsv.id,
        raw: rsv,
        code: rsv.code,
      });
    }
  });

  // 配車不可時間（デマンドブレイク）
  (userState.demandSchedules || []).flatMap((ds) => ds.demandBreaks || []).forEach((brk) => {
    if (!brk.start || !brk.end) return;
    const duration = Math.ceil(
      (new Date(brk.end).getTime() - new Date(brk.start).getTime()) / (1000 * 60),
    );
    items.push({
      start: brk.start,
      duration,
      type: 'break',
      id: brk.id,
      raw: brk,
    });
  });

  // 配車
  state.dispatches.forEach((d) => {
    const locations = (d.passengers || []).flatMap((p) => p.passengerLocations || []);
    const sorted = [...locations].sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime());
    const start = sorted[0]?.datetime;
    if (!start) return;
    let duration = 30;
    if (sorted.length >= 2) {
      const t1 = new Date(sorted[0].datetime).getTime();
      const t2 = new Date(sorted[sorted.length - 1].datetime).getTime();
      duration = Math.ceil((t2 - t1) / (1000 * 60));
    }
    const isCanceled = d.passengers.every((p) => p.flag > 900);
    if (!isCanceled) {
      items.push({
        start,
        duration,
        type: 'dispatch',
        id: d.id,
        raw: d,
        code: d.code,
      });
    }
  });

  return items.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
}

const actions = {
  async getData({ dispatch, rootState }) {
    if (_isFetching) return;
    _isFetching = true;
    try {
      await Promise.all([
        dispatch('getReservations'),
        dispatch('getDispatches'),
      ]);
      dispatch('user/setDriverStatus', rootState.user, { root: true });
    } finally {
      _isFetching = false;
    }
  },

  /** scheduleItems state を再計算してセット */
  updateScheduleItems({ commit, state, rootState }) {
    commit('setScheduleItems', buildScheduleItems(state, rootState));
  },

  /** ドライバーに紐づく予約一覧を取得 */
  async getReservations({ commit, dispatch, rootState }, args) {
    const userId = rootState.user.id;
    if (!userId) return;

    _reservationsFetchId += 1;
    const myId = _reservationsFetchId;

    const targetDate = moment().hour() < 5 ? moment().subtract(1, 'days') : moment();

    try {
      const { data } = await axios({
        method: 'GET',
        url: '/v1/reservation/get/list',
        params: {
          driver_id: userId,
          flags: [1, 10],
          byDatabase: 1,
          startDateTime: targetDate.format('YYYY-MM-DD 05:00:00'),
          withDemandCustomers: 1,
          withDemandSchedule: 1,
          withRevisions: 1,
          withMemo: 1,
          withOrderResults: 1,
          orderBy: 'rsvDatetime',
          order: 'asc',
        },
      });
      if (args?.hideLoading) dispatch('modal/loadings/hideModal', null, { root: true });
      // 最新のリクエスト結果のみ反映（古い応答は無視してレースコンディションを防ぐ）
      if (myId === _reservationsFetchId) {
        commit('setReservations', data.reservations?.data || []);
        dispatch('updateScheduleItems');
        dispatch('user/setDriverStatus', rootState.user, { root: true });
      }
    } catch (error) {
      if (error.response) console.log(error.response.data);
      else console.log(error);
    } finally {
      commit('setIsLoadedFirst', { key: 'reservations', value: true });
    }
  },

  /** ドライバーに紐づくスケジュールの配車一覧を取得 */
  async getDispatches({ commit, dispatch, rootState }, data) {
    const userId = rootState.user.id;
    if (!userId) return;

    _dispatchesFetchId += 1;
    const myId = _dispatchesFetchId;

    try {
      const schedules = rootState.user.schedules;
      if (!schedules.length) {
        // if (myId === _dispatchesFetchId) commit('setDispatches', []);
        return;
      }

      // スケジュールごとに配車を並列取得
      const results = await Promise.all(
        schedules.map((schedule) => axios({
          method: 'GET',
          url: '/v1/dispatch/get/list',
          params: {
            scheduleId: schedule.id,
            withPassengers: 1,
            withSchedule: 1,
            byDatabase: 1,
          },
        })
          .then((res) => res.data.dispatches?.data || [])
          .catch(() => [])),
      );

      if (data?.hideLoading) dispatch('modal/loadings/hideModal', null, { root: true });
      if (myId === _dispatchesFetchId) {
        commit('setDispatches', results.flat());
        dispatch('updateScheduleItems');
        dispatch('user/setDriverStatus', rootState.user, { root: true });
      }
    } catch (error) {
      if (error.response) console.log(error.response.data);
      else console.log(error);
    } finally {
      commit('setIsLoadedFirst', { key: 'dispatches', value: true });
    }
  },
};

const mutations = {
  setReservations(state, reservations) {
    state.reservations = reservations;
  },
  setDispatches(state, dispatches) {
    // 開始時間の早い順にして格納
    state.dispatches = dispatches.sort((a, b) => {
      const startTime = (d) => {
        const locationTimes = d.passengers.flatMap((p) => p.passengerLocations).map((l) => new Date(l.datetime).getTime());
        return locationTimes.length ? Math.min(...locationTimes) : Infinity;
      };
      return startTime(a) - startTime(b);
    });
  },
  setScheduleItems(state, items) {
    state.scheduleItems = items;
  },
  setIsLoadedFirst(state, { key, value }) {
    if (key in state.isLoadedFirst) state.isLoadedFirst[key] = value;
    state.isLoadedFirst.all = Object.entries(state.isLoadedFirst).every(([k, v]) => Boolean(v) || k === 'all');
  },
};

const state = {
  reservations: [],
  dispatches: [],
  scheduleItems: [],
  isLoadedFirst: {
    all: false,
    reservations: false,
    dispatches: false,
  },
};

export default {
  namespaced: true,
  actions,
  mutations,
  state,
};
